import { DateService, LoggerService, StateService, TabManagerService } from '@pulse/browser-shared';
import { PluginHost } from '@pulse/browser-tracker-shared';
import {
    AbstractPulseTracker,
    PulseAttribute,
    PulseAttributeHint,
    PulseCommand,
    PulseCommandName,
    PulseEvent,
    PulseMessage,
    PulseMessagePriority,
    PulseMessageType,
    TrackerConfigurations,
    objectDeepClone,
} from '@pulse/common';
import { Subject } from 'rxjs';
import { PulsePluginContext } from './context';
import { PipeHost } from './pipe';
import { SessionService } from './services';
import { BatchQueue, MessageQueue } from './transport';

export class PulseTracker extends AbstractPulseTracker {
    constructor(
        public readonly plugins: PluginHost,
        public readonly pipes: PipeHost,
        public readonly logger: LoggerService,
        private readonly defaultPipes: PipeHost,
        private readonly queue: MessageQueue,
        private readonly sessionService: SessionService,
        private readonly tabManagerService: TabManagerService,
        private readonly date: DateService,
        private readonly config: TrackerConfigurations,
        private readonly stateService: StateService,
        private readonly batchQueue: BatchQueue,
    ) {
        super();
        const context = this.getContext();
        plugins.init(context);
    }

    /**
     * Enqueue an event to be sent.
     */
    event<T = Record<string, unknown>>(
        event: PulseEvent<T>,
        priority: PulseMessagePriority = PulseMessagePriority.Normal,
    ): void {
        this.events([event], priority);
    }
    events<T = Record<string, unknown>>(
        events: PulseEvent<T>[],
        priority: PulseMessagePriority = PulseMessagePriority.Normal,
    ): void {
        this.processMessages('event', events, priority);
    }

    /**
     * Create or modify device level attribute.
     */
    attribute(
        attribute: PulseAttribute | Record<string, unknown>,
        priority: PulseMessagePriority = PulseMessagePriority.Normal,
    ): void {
        this.attributes([attribute], priority);
    }
    attributes(
        attributes: (PulseAttribute | Record<string, unknown>)[],
        priority: PulseMessagePriority = PulseMessagePriority.Normal,
    ): void {
        this.processMessages('attribute', attributes, priority);
    }

    /**
     * Send attribute hint.
     * Attribute hints are small piece of information that will be processed
     * by the tracking server to produce some predefined attributes such as
     * browser version, screen resolution, etc.
     */
    attributeHint(
        attribute: PulseAttributeHint | Record<string, unknown>,
        priority: PulseMessagePriority = PulseMessagePriority.Normal,
    ): void {
        this.processMessages('attribute-hint', [attribute], priority);
    }

    /**
     * Set session level attribute to be sent with all events.
     */
    set(state: Record<string, unknown>): void {
        if (typeof state !== 'object' || !state || Array.isArray(state)) {
            return;
        }

        // Special handling for language_code removal (legacy behavior)
        const stateToSet = { ...state };
        delete stateToSet['language_code'];

        this.stateService.setState(stateToSet);
    }

    /**
     * Legacy alias for set().
     */
    state(state: Record<string, unknown>): void {
        this.set(state);
    }

    stateData(state: Record<string, unknown>): void {
        const oldStateData = this.stateService.getStateInternal()['data'] as Record<
            string,
            unknown
        >;
        this.stateService.setState({
            data: {
                ...oldStateData,
                ...state,
            },
        });
    }

    getState(): Record<string, unknown> {
        return this.stateService.getState();
    }

    /**
     * Invoke a method using command syntax.
     *
     * @example
     * cmd('state', { user_id: 'xyz' });
     * cmd('event', { action: 'app_open', parameters: {...} });
     * cmd('attribute', { subscription_status: 'subscribed' });
     * cmd('attribute', { operation: '$inc', name: 'time_in_app', value: 15000 });
     */
    cmd(cmd: PulseCommand): void;
    cmd(cmd: PulseCommandName, ...args: unknown[]): void;
    cmd(cmd: PulseCommandName | PulseCommand, ...args: unknown[]): void {
        if (Array.isArray(cmd)) {
            return this.cmd(cmd[0], ...cmd.slice(1));
        }

        const nameParts = cmd.split('.');
        const command =
            PulseTracker.commandMap.get(nameParts[1] || nameParts[0] || '') || nameParts[0] || '';

        const handler: unknown = (this as any)[command];

        if (typeof handler !== 'function') {
            this.logger.warn('Tracker', `Pulse command '${cmd}' not found!`);

            return;
        }

        handler.apply(this, args);
    }

    flush(): void {
        this.queue.flush();
    }

    getContext(): PulsePluginContext {
        const context: PulsePluginContext = {
            config: this.config,
            session: this.sessionService.getSession(),
            tracker: this,
        };

        return context;
    }

    getTabId() {
        return this.tabManagerService.getTabId();
    }

    queueEmpty(): Subject<boolean> {
        return this.batchQueue.getQueueEmpty$();
    }

    private processMessages(
        type: PulseMessageType,
        data: any[],
        priority: PulseMessagePriority,
    ): void {
        const context = this.getContext();

        const messages: PulseMessage[] = data.map((message) => {
            let data;
            try {
                data = objectDeepClone(message);
            } catch (err) {
                console.error('The event provided has an incorrect schema.', err);
            }

            return {
                type,
                data,
                priority,
                state: context.session.state,
                timestamp: this.date.now(),
            };
        });

        const enrichedMessages: PulseMessage[] = messages.flatMap((message) =>
            this.pipes
                .transform(message, context)
                .map((msg) => this.defaultPipes.transform(msg, context))
                .flat()
                .map((msg) => this.defaultPipes.enrich(msg, context))
                .map((msg) => this.pipes.enrich(msg, context)),
        );

        this.queue.push(...enrichedMessages);
    }
}
